import { FileUploader, UploadedFile, UploadedFileRow } from './Files';
import { Form, FormOverlay, ResponseInput } from '../tableItemDetailViewComponents/FormComponents';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import styled from 'styled-components';
import { FileType, NewCommentFile } from '../../api';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { createTrackingProps } from 'shared-utils/analytics';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { StyledContainer } from '../tableItemDetailViewComponents/Containers';
import { validateFiles } from '../../utils';

const StyledForm = styled(Form)`
  position: relative;
  padding: 0;
  textarea {
    padding: 16px;
  }
`;

const FormContainer = styled(StyledContainer)<{ $renderAsCard?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${({ $renderAsCard }) => ($renderAsCard ? `padding: 16px;` : `border: none; box-shadow: none;`)}
`;

type CloseButtonHidden = {
  visible?: false;
  onClick?: undefined;
  isLoading?: undefined;
  disabled?: undefined;
  disabledTooltipText?: undefined;
};

type CloseButtonVisible = {
  visible: true;
  onClick: () => void;
  isLoading?: boolean;
};

type CloseButtonEnabled = CloseButtonVisible & {
  disabled?: false | undefined;
  disabledTooltipText?: undefined;
};

type CloseButtonDisabled = CloseButtonVisible & {
  disabled: true;
  disabledTooltipText?: string;
};

export type CloseButtonProps = CloseButtonHidden | CloseButtonEnabled | CloseButtonDisabled;

type CommentAndFileSectionProps = {
  onSubmit?: () => void;
  isSubmitting?: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  placeholder?: string;
  submitButtonText?: string;
  hideSubmitButton?: boolean;
  files: NewCommentFile[];
  onFilesChange: (files: NewCommentFile[]) => void;
  errors?: string[];
  renderAsCard?: boolean;
  inputValue: string;
  setInputValue: (value: string) => void;
  autoFocus?: boolean;
  fileTypesOptions?: FileType[];
  labelId?: string;
  className?: string;
  // TODO: group submit button props into a single object like this
  closeButtonProps?: CloseButtonProps;
  /**
   * Extra elements to render on the right side of the button row (but to the left of the close/submit buttons)
   */
  extraRightSideElements?: React.ReactNode;
};

const CommentAndFileSection = ({
  onSubmit,
  isSubmitting = false,
  inputRef,
  placeholder = 'Add response...',
  submitButtonText = 'Submit',
  hideSubmitButton = false,
  files,
  onFilesChange,
  errors,
  renderAsCard = false,
  inputValue,
  setInputValue,
  autoFocus = false,
  fileTypesOptions,
  labelId,
  className,
  closeButtonProps,
  extraRightSideElements,
}: CommentAndFileSectionProps) => {
  const [duplicateFileNameUploaded, setDuplicateFileNameUploaded] = useState<string | null>(null);
  const [fileValidationError, setFileValidationError] = useState<string | null>(null);

  const onFilesUpload = (filesToUpload: NewCommentFile[]) => {
    setDuplicateFileNameUploaded(null);
    setFileValidationError(null);

    // Validate files first
    const validationError = validateFiles(filesToUpload);
    if (validationError) {
      setFileValidationError(validationError);
      return;
    }

    let foundDuplicateFileName = false;

    // Error out if the user happened to choose a file with the same name (path) as one they've
    // already added. Normally it wouldn't matter, but we're using the file names as keys to track
    // the document types when sending them to the server for saving. If they upload another file
    // with the same name, they will both be uploaded with the same document type, which will
    // cause problems downstream.
    filesToUpload.map((fileToUpload) => {
      const fileNameAlreadyUploaded = files.find((uploadedFile: NewCommentFile) => {
        return fileToUpload.file.name === uploadedFile.file.name;
      });
      if (fileNameAlreadyUploaded) {
        setDuplicateFileNameUploaded(fileToUpload.file.name);
        // We're using a local variable because the state won't update fast enough for us to use
        // that value
        foundDuplicateFileName = true;
        return;
      }
    });

    if (!foundDuplicateFileName) {
      onFilesChange([...files, ...filesToUpload]);
    }
  };

  const onFileDelete = (filename: string, document_type: FileType) => {
    const filesCopy = [...files];

    const fileIndex = filesCopy.findIndex((fileInfo) => {
      return fileInfo.file.name === filename && fileInfo.document_type === document_type;
    });

    if (fileIndex === -1) {
      toast.custom(() => <StandardErrorToast />);
      console.error("Couldn't delete file because we couldn't find it: ", filename);
    } else {
      filesCopy.splice(fileIndex, 1);
      onFilesChange(filesCopy);
    }
  };

  // auto-focus on the input field when it becomes visible
  // used in places where the input field is hidden to begin with and then becomes visible
  // on user action (e.g. clicking an "Add a response" button)
  useEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.focus();
    }
  }, [inputRef, autoFocus]);

  let submitButton: React.ReactNode = null;
  if (!hideSubmitButton) {
    submitButton = (
      <Button
        {...createTrackingProps({ elementName: 'Submit Comment Button' })}
        onClick={onSubmit}
        disabled={!inputValue && !files.length}
        isLoading={isSubmitting}
        size="sm"
      >
        {submitButtonText}
      </Button>
    );
  }

  let closeButton: React.ReactNode = null;
  if (closeButtonProps?.visible) {
    const { onClick, isLoading, disabled, disabledTooltipText } = closeButtonProps as Exclude<
      CloseButtonProps,
      { visible?: false }
    >;
    const button = (
      <Button
        {...createTrackingProps({ elementName: 'Close Task Button' })}
        variant="tertiary"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        isLoading={isLoading}
      >
        Close task
      </Button>
    );
    closeButton =
      disabled && disabledTooltipText ? (
        <Tooltip content={disabledTooltipText} childNotInteractive>
          {button}
        </Tooltip>
      ) : (
        button
      );
  }

  return (
    <FormContainer $renderAsCard={renderAsCard} className={className}>
      <StyledForm>
        {/* TODO: remove this and disable the input field instead. the submit button can handle its own loading state */}
        {isSubmitting && (
          <FormOverlay>
            <LoadingSpinner />
          </FormOverlay>
        )}
        <ResponseInput
          ref={inputRef}
          placeholder={placeholder}
          value={inputValue}
          // this is necessary for enabling the submit button disabled state to react to the input value
          onChange={(e) => setInputValue(e.target.value)}
          aria-labelledby={labelId}
        />
      </StyledForm>

      {files.length > 0 && (
        <UploadedFileRow>
          {files.map((fileInfo) => (
            <UploadedFile
              key={`${fileInfo.file.name}`}
              onFileDelete={onFileDelete}
              filename={fileInfo.file.name}
              fileType={fileInfo.document_type}
            />
          ))}
        </UploadedFileRow>
      )}

      <ButtonRow align={Alignment.SpaceBetween}>
        <FileUploader onFilesUpload={onFilesUpload} fileTypesOptions={fileTypesOptions} showAttachButtonLabel />
        <ButtonRow align={Alignment.Right}>
          {extraRightSideElements}
          {closeButton}
          {submitButton}
        </ButtonRow>
      </ButtonRow>

      {errors && errors.map((error) => <ErrorMessage visible={files.length > 0}>{error}</ErrorMessage>)}
      {/* TODO: move duplicate file name error into fileValidationError */}
      <ErrorMessage visible={!!duplicateFileNameUploaded}>
        {`A file with the name ${duplicateFileNameUploaded || ''} has already been added. Please change the file name and try again.`}
      </ErrorMessage>
      <ErrorMessage visible={!!fileValidationError}>{fileValidationError}</ErrorMessage>
    </FormContainer>
  );
};

export default CommentAndFileSection;
